import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  ChevronDown,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Mail,
  Users,
  Heart,
  Calendar,
  FileText,
  Activity,
  ArrowUpRight,
  Download,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

/**
 * AdminFamiliesPage - Manage all family accounts
 * 
 * Features:
 * - List all families with search/filter
 * - Quick stats per family (children, members, activity)
 * - Drill down into family details
 * - View as family (impersonate for support)
 * - Bulk actions
 */
export default function AdminFamiliesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);

  // Mock data - would come from API
  const families = [
    {
      id: 'fam-1',
      name: 'Johnson Family',
      primaryContact: 'Sarah Johnson',
      email: 'sarah.johnson@email.com',
      members: 4,
      children: 2,
      status: 'active',
      plan: 'premium',
      createdAt: '2024-06-15',
      lastActivity: '2 hours ago',
      healthLogs: 156,
      documents: 34,
    },
    {
      id: 'fam-2',
      name: 'Chen Family',
      primaryContact: 'Michael Chen',
      email: 'michael.chen@email.com',
      members: 3,
      children: 1,
      status: 'active',
      plan: 'basic',
      createdAt: '2024-08-22',
      lastActivity: '1 day ago',
      healthLogs: 89,
      documents: 12,
    },
    {
      id: 'fam-3',
      name: 'Davis Family',
      primaryContact: 'Emily Davis',
      email: 'emily.davis@email.com',
      members: 2,
      children: 1,
      status: 'inactive',
      plan: 'basic',
      createdAt: '2024-03-10',
      lastActivity: '2 weeks ago',
      healthLogs: 45,
      documents: 8,
    },
    {
      id: 'fam-4',
      name: 'Rodriguez Family',
      primaryContact: 'Maria Rodriguez',
      email: 'maria.r@email.com',
      members: 5,
      children: 3,
      status: 'active',
      plan: 'premium',
      createdAt: '2024-01-05',
      lastActivity: '5 hours ago',
      healthLogs: 312,
      documents: 67,
    },
  ];

  const stats = {
    total: 1247,
    active: 1189,
    premium: 342,
    newThisMonth: 87,
  };

  const statusColors = {
    active: 'bg-sage/10 text-sage',
    inactive: 'bg-warm-gray/10 text-warm-gray',
    suspended: 'bg-error/10 text-error',
  };

  const planColors = {
    basic: 'bg-warm-gray/10 text-warm-gray',
    premium: 'bg-navy/10 text-navy',
    enterprise: 'bg-terracotta/10 text-terracotta',
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-display-sm text-navy">Families</h1>
          <p className="text-warm-gray mt-1">
            Manage family accounts and drill down into their data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="primary" size="sm">
            <Users className="w-4 h-4 mr-2" />
            Add Family
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-4 gap-4">
        <StatCard label="Total Families" value={stats.total} />
        <StatCard label="Active" value={stats.active} trend="+5%" />
        <StatCard label="Premium Plans" value={stats.premium} />
        <StatCard label="New This Month" value={stats.newThisMonth} trend="+12%" />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
            <Input
              placeholder="Search families..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select defaultValue="all">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </Select>
          <Select defaultValue="all">
            <option value="all">All Plans</option>
            <option value="basic">Basic</option>
            <option value="premium">Premium</option>
            <option value="enterprise">Enterprise</option>
          </Select>
          <Button variant="outline">
            <Filter className="w-4 h-4 mr-2" />
            More Filters
          </Button>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedFamilies.length > 0 && (
        <Card className="p-4 bg-sage/5 border-sage/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-navy">
              {selectedFamilies.length} families selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">Send Email</Button>
              <Button variant="outline" size="sm">Export Data</Button>
              <Button variant="outline" size="sm" className="text-error">
                Suspend
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Families Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream/50">
              <tr>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  <input 
                    type="checkbox"
                    className="rounded border-warm-gray/30"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFamilies(families.map(f => f.id));
                      } else {
                        setSelectedFamilies([]);
                      }
                    }}
                  />
                </th>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Family
                </th>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Members
                </th>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Activity
                </th>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Status
                </th>
                <th className="text-left text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Plan
                </th>
                <th className="text-right text-xs font-medium text-warm-gray uppercase tracking-wide px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-gray/10">
              {families.map((family) => (
                <tr key={family.id} className="hover:bg-cream/30 transition-colors">
                  <td className="px-4 py-4">
                    <input 
                      type="checkbox"
                      className="rounded border-warm-gray/30"
                      checked={selectedFamilies.includes(family.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFamilies([...selectedFamilies, family.id]);
                        } else {
                          setSelectedFamilies(selectedFamilies.filter(id => id !== family.id));
                        }
                      }}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Link to={`/admin/families/${family.id}`} className="group">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-sage" />
                        </div>
                        <div>
                          <p className="font-medium text-navy group-hover:text-sage transition-colors">
                            {family.name}
                          </p>
                          <p className="text-sm text-warm-gray">{family.email}</p>
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-warm-gray" />
                        {family.members}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4 text-warm-gray" />
                        {family.children} children
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <p className="text-navy">{family.lastActivity}</p>
                      <p className="text-warm-gray text-xs">
                        {family.healthLogs} logs • {family.documents} docs
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <Badge className={statusColors[family.status as keyof typeof statusColors]}>
                      {family.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-4">
                    <Badge className={planColors[family.plan as keyof typeof planColors]}>
                      {family.plan}
                    </Badge>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link 
                        to={`/admin/families/${family.id}`}
                        className="p-2 rounded-lg hover:bg-cream transition-colors text-warm-gray hover:text-navy"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <Link 
                        to={`/admin/families/${family.id}/view-as`}
                        className="p-2 rounded-lg hover:bg-sage/10 transition-colors text-warm-gray hover:text-sage"
                        title="View as Family"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-lg hover:bg-cream transition-colors text-warm-gray">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Family
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Activity className="w-4 h-4 mr-2" />
                            View Activity
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <FileText className="w-4 h-4 mr-2" />
                            Export Data
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-error">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Suspend Family
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-warm-gray/10 flex items-center justify-between">
          <p className="text-sm text-warm-gray">
            Showing 1-10 of {stats.total} families
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled>Previous</Button>
            <Button variant="outline" size="sm">Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  trend 
}: { 
  label: string; 
  value: number; 
  trend?: string 
}) {
  return (
    <Card className="p-4">
      <p className="text-sm text-warm-gray mb-1">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-display font-semibold text-navy">
          {value.toLocaleString()}
        </span>
        {trend && (
          <span className="text-sm text-sage font-medium">{trend}</span>
        )}
      </div>
    </Card>
  );
}
